import { useEffect, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash/fp";

import { getQueryKey as getTopicQueryKey } from "@app/analytics/TopicReport/hooks/useTopicReport";
import api from "@app/common/access/ShareReport/api";
import useAuth from "@app/common/auth/useAuth";
import useUserMetadata from "@app/common/auth/useUserMetadata";
import type { UserMetadata } from "@app/common/types";

export const getAccessQueryKey = (
  sessionId: string
): [unknown, { sessionId: string }] => ["reportAccessList", { sessionId }];

const acceptInvite = async ({
  sessionId,
  token,
  reportType,
}: {
  sessionId: string;
  token: string;
  reportType: "topic" | "meeting";
}) => {
  return api.access.acceptInvite(sessionId, token, reportType);
};

const useReportAcceptInvite = (
  // format [topic:]reportId:token
  reportInvite: string | null,
  options?: {
    enabled?: boolean;
    onSettled?: () => void;
    onError?: () => void;
    signupSource?: UserMetadata["signupSource"];
  }
) => {
  let reportInviteToken: string | null = null;
  let reportInviteReportId: string | null = null;
  let reportType: "topic" | "meeting";
  if (reportInvite) {
    const parts = reportInvite.split(":");

    if (parts[0] === "topic") {
      reportType = "topic";
      [reportInviteReportId, reportInviteToken] = [
        parts[1],
        parts.slice(2).join(":"),
      ];
    } else {
      reportType = "meeting";
      [reportInviteReportId, reportInviteToken] = [
        parts[0],
        parts.slice(1).join(":"),
      ];
    }
  }

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { userMetadata, updateUserMetadata } = useUserMetadata();
  const [attemptedInvite, setAttemptedInvite] = useState<string | null>(null);
  const hasInvite = !isEmpty(reportInviteToken);
  const enabled = options?.enabled ?? true;

  const shouldInvite =
    enabled && hasInvite && reportInviteReportId !== attemptedInvite;

  const acceptInviteMutation = useMutation({
    mutationFn: acceptInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getAccessQueryKey(reportInviteReportId!),
      });
      queryClient.invalidateQueries({
        queryKey: getTopicQueryKey({
          topicId: reportInviteReportId!,
          userId: user?.id,
        }),
      });
    },
  });

  useEffect(() => {
    if (shouldInvite && reportInviteToken) {
      // don't override a signup source if one has already been set
      if (options?.signupSource && userMetadata?.signupSource == null) {
        updateUserMetadata.mutate({ signupSource: options.signupSource });
      }
      acceptInviteMutation.mutate(
        {
          sessionId: reportInviteReportId!,
          token: reportInviteToken,
          reportType,
        },
        {
          onError: options?.onError,
          onSettled: () => setAttemptedInvite(reportInviteReportId),
        }
      );
    }
  }, [reportInvite, shouldInvite, options?.signupSource]);

  return { isLoading: acceptInviteMutation.isPending || shouldInvite };
};

export default useReportAcceptInvite;
